import {getMetaContent, getSelectedFilter, getShopToDyFilterMapping} from "./Util";
import Cookies from "js-cookie";

class DynamicYieldTracking {

	static debugMode = false;

	constructor() {
		this.currency = getMetaContent( '_isoCurrency' ) || 'EUR';

		this.initBasketUpdate();
		this.initWishlistUpdate();
		this.initCheckoutThankyou();
		this.initSearch();
		this.initLogin();
		this.initSignup();
		this.initNewsletterSubscription();
		this.initIdentifyUser();
		this.initSortItems();
		this.initFilterItems();
		this.initProductClick();
	}

	initBasketUpdate() {
		window.addEventListener( 'minibasket_changed.acl', ( ev ) => {
			const { basketChanged, basketItems, totalValue }  = ev.detail;

			// Skip on thankyou page
			if ( window.v_cl === 'thankyou' ) {
				return;
			}

			// Fire add to or remove from cart event
			basketChanged.forEach( ( changedBasketItem ) => {
				switch( changedBasketItem.action ) {
					case 'add':
						this.sendAddToCart( changedBasketItem.item, changedBasketItem.diffamount, changedBasketItem.value, basketItems );
						break;
					case 'remove':
						this.sendRemoveFromCart( changedBasketItem.item, changedBasketItem.diffamount, changedBasketItem.value, basketItems );
						break;
				}
			} );

			//Fire sync cart event
			this.sendSyncCart( basketItems, totalValue );
		} );
	}

	initWishlistUpdate() {
		window.addEventListener( 'wishlist_updated.acl', ( ev ) => {
			const articleId = ev.detail.articleId;
			const action = ev.detail.action;

			if( action === 'add' ) {
				//Fire add to wishlist event
				this.sendAddToWishlist( articleId );
			}
		} );
	}

	initCheckoutThankyou() {
		document.addEventListener( 'checkout_thankyou.acl', ( ev ) => {
			const orderId = ev.detail.orderId;
			const totalValue = ev.detail.totalValue;
			const products = ev.detail.products;

			this.sendPurchase( orderId, totalValue, products );
		} );
	}

	initSearch() {
		document.addEventListener( 'perform_search.sl', ( ev ) => {
			const term = ev.detail.term;

			this.sendKeywordSearch( term );
		} );
	}

	initLogin() {
		// Cookie just here as fallback if init event via api was not working
		const hashedEmail = Cookies.get( 'loginTracking' );
		if( !hashedEmail ) return;

		this.sendLogin( hashedEmail );
		Cookies.remove( 'loginTracking' );
	}

	initSignup() {
		// Cookie just here as fallback if signup event via api was not working
		const hashedEmail = Cookies.get( 'registerTracking' );
		if( !hashedEmail ) return;

		this.sendSignup( hashedEmail );
		Cookies.remove( 'registerTracking' );
	}

	initNewsletterSubscription() {
		// Cookie just here as fallback if newsletter subscription event via api was not working
		const hashedEmail = Cookies.get( 'registerNewsletterTracking' );
		if( !hashedEmail ) return;

		this.sendNewsletterSubscription( hashedEmail );
		Cookies.remove( 'registerNewsletterTracking' );
	}

	initIdentifyUser() {
		// Cookie just here as fallback if identify user event via api was not working
		const hashedEmail = Cookies.get( 'identifyUserTracking' );
		if( !hashedEmail ) return;

		this.sendIdentifyUser( hashedEmail );
		Cookies.remove( 'identifyUserTracking' );
	}


	initSortItems() {
		document.addEventListener( 'sort_items.acl', ( ev ) => {
			const { sorting: rawSorting, order: rawOrder } = ev.detail;
			const mapSorting = ( value ) => {
				const key = value.toLowerCase().trim();

				switch ( key ) {
					case 'name':
					case 'price':
						return key;
					default:
						return 'OTHER';
				}
			};

			const sorting = mapSorting( rawSorting );
			const order = rawOrder.toUpperCase();

			this.sendSortItems( sorting, order );
		} );
	}

	initFilterItems() {
		window.addEventListener( 'submit_chip.acl', ( ev ) => {
			const { fieldset, identifier } = ev.detail;

			const getAssociatedDyFilterName = ( key ) => {
				// Map filters to the appropriate dy column
				return getShopToDyFilterMapping()?.[key] ?? false;
			}

			//Could be more than one because one identifier could have multiple filters (multi)
			Object.entries( getSelectedFilter( fieldset, identifier ) ).forEach( ( [key, values] ) => {
				// Get associated dy filter, if not available it returns false
				const dyFilterName = getAssociatedDyFilterName( key );
				if( !dyFilterName ) return

				if ( Array.isArray( values ) ) {
					values.forEach( value => this.sendFilterItems( dyFilterName, value ) );
				} else {
					this.sendFilterItems( dyFilterName, values );
				}
			} );
		} );
	}

	// Add to Cart - Reports that the user added a product to the cart. (https://dy.dev/docs/add-to-cart)
	sendAddToCart( basketItem, quantity, totalValue, basketItems ) {
		this.sendDYEvent( 'Add to Cart', 'add-to-cart-v1', {
			value: totalValue,
			currency: this.currency,
			productId: basketItem.oxid,
			quantity: quantity,
			cart: this.getDYCartObject( basketItems )
		} );
	}

	// Remove from Cart - Reports that the user removed an item from the cart. (https://dy.dev/docs/remove-from-cart)
	sendRemoveFromCart( basketItem, quantity, totalValue, basketItems ) {
		this.sendDYEvent( 'Remove from Cart', 'remove-from-cart-v1', {
			value: totalValue,
			currency: this.currency,
			productId: basketItem.oxid,
			quantity: quantity,
			cart: this.getDYCartObject( basketItems )
		} );
	}

	// Sync Cart - Updates the current cart state. (https://dy.dev/docs/sync-cart)
	sendSyncCart( basketItems, totalValue ) {
		this.sendDYEvent( 'Sync cart', 'sync-cart-v1', {
			value: totalValue,
			currency: this.currency,
			cart: this.getDYCartObject( basketItems )
		} );
	}

	// Add to Wishlist - Reports that a user added a product to their wishlist. (https://dy.dev/docs/wishlist)
	sendAddToWishlist( articleId ) {
		this.sendDYEvent( 'Add to Wishlist', 'add-to-wishlist-v1', {
			productId: articleId,
		} );
	}

	//Purchase - Reports that the user completed a purchase. (https://dy.dev/docs/purchase)
	sendPurchase( orderId, totalValue, products ) {
		this.sendDYEvent( 'Purchase', 'purchase-v1', {
			value: totalValue,
			uniqueTransactionId: orderId,
			cart: products.map( ( { dimension10: productId, quantity: quantity, price: itemPrice } ) => ( { productId, quantity, itemPrice } ) )
		} );
	}

	// Keyword Search - Reports that a user ran a freestyle keyword search. (https://dy.dev/docs/keyword-search)
	sendKeywordSearch( term ) {
		this.sendDYEvent( 'Keyword Search', 'keyword-search-v1', {
			keywords: term,
		} );
	}

	// Identify User - Reports a user identification (https://dy.dev/docs/identify-user)
	sendIdentifyUser( hashedEmail ) {
		this.sendDYEvent( 'Identify User', 'identify-v1', {
			cuidType: 'he',
			hashedEmail: hashedEmail,
		} );
	}

	// Login - Reports that the user completed the signup process and is a registered user. Identification event. (https://dy.dev/docs/login)
	sendLogin( hashedEmail ) {
		this.sendDYEvent( 'Login', 'login-v1', {
			cuidType: 'he',
			hashedEmail: hashedEmail,
		} );
	}

	// Signup - Reports that the user completed the signup process and is a registered user. Identification event. (https://dy.dev/docs/signup)
	sendSignup( hashedEmail ) {
		this.sendDYEvent( 'Signup', 'signup-v1', {
			cuidType: 'he',
			hashedEmail: hashedEmail,
		} );
	}

	// Newsletter Subscription - Reports that a user signed up for a newsletter. Identification event. (https://dy.dev/docs/newsletter-subscription)
	sendNewsletterSubscription( hashedEmail ) {
		this.sendDYEvent( 'Newsletter Subscription', 'newsletter-subscription-v1', {
			cuidType: 'he',
			hashedEmail: hashedEmail,
		} );
	}

	// Sort Items - Reports that the user changed the sorting of a product list (https://dy.dev/docs/sort-items)
	sendSortItems( sorting, order ) {
		this.sendDYEvent( 'Sort Items', 'sort-items-v1', {
			sortBy: sorting,
			sortOrder: order,
		} );
	}

	// Filter Items - Reports that the user filtered the product list by a specific field value. (https://dy.dev/docs/filter-items)
	sendFilterItems( filterName, value ) {
		const numericValue = Number( value );
		const isNumeric = !isNaN( numericValue );

		this.sendDYEvent( 'Filter Items', 'filter-items-v1', {
			filterType: filterName,
			...( !isNumeric && {
				filterStringValue: ['sale', 'new'].includes( filterName ) ? 'true' : value,
			} ),
			...( isNumeric && {
				filterNumericValue: ['sale', 'new'].includes( filterName ) ? 'true' : value,
			} ),
		} );
	}

	initProductClick() {
		const selectors = [
			'article.en_mdl_product[data-slot-id]',
			'article.mdl_productcarousel_item[data-slot-id]',
			'.mdl_search__suggestions-product[data-slot-id]',
		];


		document.addEventListener( 'click', ( ev ) => {
			const targetEl = ev.target.closest( selectors.join( ', ' ) );
			if ( targetEl ) {
				this.sendReportEngagement( targetEl );
			}
		} );
	}

	sendReportEngagement( targetEl ) {
		if ( 'function' === typeof navigator.sendBeacon ) {

			// Endpoint.
			const url = '/index.php?cl=dynamic_yield_api&fnc=reportEngagement';

			// Setup Postdata.
			const postData = new FormData();
			postData.append( 'type', 'SLOT_CLICK' );
			postData.append( 'slotId', targetEl.getAttribute( 'data-slot-id' ) );

			if ( targetEl.dataset.variations ) {
				const variations = String( targetEl.dataset.variations ).split( ',' ) || [];
				variations.map( variation => {
					postData.append( 'variations[]', variation );
				} );
			}

			// Report Engagement to Beacon.
			return navigator.sendBeacon( url, postData );
		}

		return false;
	}

	sendDYEvent( name, dyType, properties = {} ) {
		// Check if name and dyType are set
		if ( !name || !dyType ) {
			return;
		}

		// Check if DY api is available
		if ( typeof window.DY === "undefined" || typeof window.DY.API !== 'function' ) {
			return;
		}

		if( DynamicYieldTracking.debugMode ) {
			console.log( JSON.stringify( {
				name: name,
				properties: {
					dyType: dyType,
					...properties
				}
			} ) );
		}

		window.DY.API( 'event', {
			name: name,
			properties: {
				dyType: dyType,
				...properties
			}
		} );
	}

	getDYCartObject( basketItems ) {
		return basketItems.map( ( { oxid: productId, amount: quantity, price: itemPrice } ) => ( { productId, quantity, itemPrice } ) );
	}
}

const initDynamicYieldTracking = () => {
	window.SL_DY_TRACKING ??= new DynamicYieldTracking();
	return window.SL_DY_TRACKING;
}

export {
	DynamicYieldTracking as default,
	initDynamicYieldTracking
}
